/**
 * Scroll and load animations for the GBX product launch landing page.
 * Requires gsap, ScrollTrigger and SplitText (loaded in theme.liquid for the gbx-landing template).
 */
(function () {
  function initScrollToTarget() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-scroll-target]');
      if (!trigger) return;

      var target = document.querySelector(trigger.getAttribute('data-scroll-target'));
      if (!target) return;

      event.preventDefault();

      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var smoother = typeof ScrollSmoother !== 'undefined' ? ScrollSmoother.get() : null;

      if (smoother) {
        smoother.scrollTo(target, !reduceMotion, 'top top');
        return;
      }

      var nav = document.querySelector('.gbx-hero__nav');
      var offset = nav ? nav.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  initScrollToTarget();

  function initGbxAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(initGbxAnimations, 100);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    if (typeof ScrollSmoother !== 'undefined') {
      gsap.registerPlugin(ScrollSmoother);
    }

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    initSmoothScroll();
    initHero();
    initStickyNav();
    initIntroTextFill();
    initStats();
    initConditions();
    initMaterials();
    initVideo();
    initFinish();
    initFooterCta();

    ScrollTrigger.refresh();
  }

  function initSmoothScroll() {
    if (typeof ScrollSmoother === 'undefined') return;
    if (!document.getElementById('gbx-smooth-wrapper')) return;

    ScrollSmoother.create({
      wrapper: '#gbx-smooth-wrapper',
      content: '#gbx-smooth-content',
      smooth: 0.8,
      normalizeScroll: true,
      ignoreMobileResize: true,
      // Whole-pixel scroll offsets avoid the sub-pixel transform values that
      // make the 1px section-divider borders shimmer while scrolling.
      wholePixels: true,
    });
  }

  function initHero() {
    var hero = document.querySelector('.gbx-hero');
    if (!hero) return;

    var logo = hero.querySelector('.gbx-hero__logo');
    var navActions = hero.querySelector('.gbx-hero__nav-actions');
    var titleLines = hero.querySelectorAll('.gbx-hero__title-line');
    var description = hero.querySelector('.gbx-hero__description');
    var actions = hero.querySelector('.gbx-hero__actions');
    var media = hero.querySelector('.gbx-hero__media');

    var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (logo) tl.from(logo, { opacity: 0, y: -16, duration: 0.6 }, 0);
    if (navActions) tl.from(navActions, { opacity: 0, y: -16, duration: 0.6 }, 0.1);
    if (media) tl.from(media, { opacity: 0, scale: 1.06, duration: 1.2, ease: 'power2.out' }, 0.15);
    if (titleLines.length) tl.from(titleLines, { opacity: 0, y: 40, duration: 0.8, stagger: 0.12 }, 0.25);
    if (description) tl.from(description, { opacity: 0, y: 24, duration: 0.7 }, '-=0.35');
    if (actions) tl.from(actions, { opacity: 0, y: 24, duration: 0.7 }, '-=0.45');
  }

  function initStickyNav() {
    var hero = document.querySelector('.gbx-hero');
    var nav = hero && hero.querySelector('.gbx-hero__nav');
    if (!hero || !nav) return;

    // Reserve the nav's original space so removing it from the hero doesn't
    // shift the content below it upward.
    var spacer = document.createElement('div');
    spacer.className = 'gbx-hero__nav-spacer';
    nav.insertAdjacentElement('afterend', spacer);

    function syncSpacerHeight() {
      spacer.style.height = nav.offsetHeight + 'px';
    }
    syncSpacerHeight();
    window.addEventListener('resize', syncSpacerHeight);

    // ScrollSmoother scrolls the page by translating its content wrapper,
    // which breaks CSS `position: sticky` for anything nested inside it.
    // GSAP's own pin can compensate for that in most cases, but pinning for
    // the whole remaining page (rather than a short scrub distance) renders
    // the nav behind later sections instead of on top of them here. Moving
    // it to a real `position: fixed` element outside the transformed tree
    // is what actually keeps it on top for the whole page.
    document.body.insertBefore(nav, document.body.firstChild);
    nav.classList.add('gbx-hero__nav--fixed');

    ScrollTrigger.create({
      trigger: hero,
      start: 'top top-=10',
      onEnter: function () { nav.classList.add('is-scrolled'); },
      onLeaveBack: function () { nav.classList.remove('is-scrolled'); },
    });
  }

  function initIntroTextFill() {
    var section = document.querySelector('.gbx-intro');
    if (!section || typeof SplitText === 'undefined') return;

    var statement = section.querySelector('.gbx-intro__statement');
    var text = section.querySelector('.gbx-intro__text');
    var words = [];

    if (statement) {
      words = words.concat(new SplitText(statement, { type: 'words' }).words);
    }
    if (text) {
      words = words.concat(new SplitText(text, { type: 'words' }).words);
    }

    if (!words.length) return;

    gsap.set(words, { opacity: 0.25, filter: 'blur(6px)', willChange: 'opacity, filter' });

    gsap.to(words, {
      opacity: 1,
      filter: 'blur(0px)',
      stagger: 1,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        end: 'bottom 40%',
        scrub: 0.6,
        onLeave: function () {
          gsap.set(words, { clearProps: 'willChange' });
        },
      },
    });
  }

  function initStats() {
    var container = document.querySelector('.gbx-stats__container');
    var items = document.querySelectorAll('.gbx-stats__item');
    if (!container || !items.length) return;

    gsap.from(items, {
      opacity: 0,
      y: 40,
      duration: 0.7,
      ease: 'power3.out',
      stagger: 0.15,
      scrollTrigger: {
        trigger: container,
        start: 'top 80%',
        once: true,
      },
    });
  }

  function initConditions() {
    var rows = document.querySelectorAll('.gbx-conditions__row');

    rows.forEach(function (row) {
      var isRight = row.classList.contains('gbx-conditions__row--image-right');
      var media = row.querySelector('.gbx-conditions__media');
      var label = row.querySelector('.gbx-conditions__label');
      var tags = row.querySelector('.gbx-conditions__tags');
      var checklistItems = row.querySelectorAll('.gbx-conditions__checklist li');

      var tl = gsap.timeline({
        defaults: { ease: 'power3.out', duration: 0.8 },
        scrollTrigger: {
          trigger: row,
          start: 'top 75%',
          once: true,
        },
      });

      if (media) tl.from(media, { opacity: 0, x: isRight ? 60 : -60 }, 0);
      if (label) tl.from(label, { opacity: 0, y: 24 }, 0.15);
      if (tags) tl.from(tags, { opacity: 0, y: 20 }, 0.25);
      if (checklistItems.length) {
        tl.from(checklistItems, { opacity: 0, x: -12, duration: 0.5, stagger: 0.08 }, 0.35);
      }
    });
  }

  function initMaterials() {
    var section = document.querySelector('.gbx-materials');
    if (!section) return;

    var headingLines = section.querySelectorAll('.gbx-materials__heading-line');
    var subtext = section.querySelector('.gbx-materials__subtext');
    var tags = section.querySelectorAll('.gbx-materials__tag');
    var specialist = section.querySelector('.gbx-materials__specialist');
    var image = section.querySelector('.gbx-materials__right');

    var tl = gsap.timeline({
      defaults: { ease: 'power3.out', duration: 0.7 },
      scrollTrigger: {
        trigger: section,
        start: 'top 70%',
        once: true,
      },
    });

    if (headingLines.length) tl.from(headingLines, { opacity: 0, y: 30, stagger: 0.1 }, 0);
    if (subtext) tl.from(subtext, { opacity: 0, y: 20 }, 0.15);
    if (image) tl.from(image, { opacity: 0, scale: 0.96 }, 0.1);
    if (tags.length) tl.from(tags, { opacity: 0, y: 16, duration: 0.4, stagger: 0.04 }, 0.3);
    if (specialist) tl.from(specialist, { opacity: 0, y: 20 }, 0.45);
  }

  function initVideo() {
    var section = document.querySelector('.gbx-video');
    if (!section) return;

    var container = section.querySelector('.gbx-video__container');
    if (!container) return;

    gsap.from(container, {
      opacity: 0,
      y: 40,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        once: true,
      },
    });
  }

  function initFinish() {
    var section = document.querySelector('.gbx-finish');
    if (!section) return;

    var media = section.querySelector('.gbx-finish__media');
    var headingLines = section.querySelectorAll('.gbx-finish__heading-line');
    var eyebrow = section.querySelector('.gbx-finish__eyebrow');
    var specs = section.querySelectorAll('.gbx-finish__spec');

    var tl = gsap.timeline({
      defaults: { ease: 'power3.out', duration: 0.7 },
      scrollTrigger: {
        trigger: section,
        start: 'top 70%',
        once: true,
      },
    });

    if (media) tl.from(media, { opacity: 0, x: -40 }, 0);
    if (headingLines.length) tl.from(headingLines, { opacity: 0, y: 24, stagger: 0.1 }, 0.1);
    if (eyebrow) tl.from(eyebrow, { opacity: 0, y: 16 }, 0.25);
    if (specs.length) tl.from(specs, { opacity: 0, y: 20, duration: 0.5, stagger: 0.08 }, 0.35);
  }

  function initFooterCta() {
    var section = document.querySelector('.gbx-cta-footer');
    if (!section) return;

    var headingLines = section.querySelectorAll('.gbx-cta-footer__heading-line');
    var text = section.querySelector('.gbx-cta-footer__text');
    var button = section.querySelector('.gbx-cta-footer__button');
    var links = section.querySelectorAll('.gbx-cta-footer__link');

    var tl = gsap.timeline({
      defaults: { ease: 'power3.out', duration: 0.7 },
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        once: true,
      },
    });

    if (headingLines.length) tl.from(headingLines, { opacity: 0, y: 30, stagger: 0.1 }, 0);
    if (text) tl.from(text, { opacity: 0, y: 20 }, 0.2);
    if (button) tl.from(button, { opacity: 0, y: 16 }, 0.3);
    if (links.length) tl.from(links, { opacity: 0, y: 12, duration: 0.4, stagger: 0.05 }, 0.35);
  }

  function initWhenReady() {
    document.fonts.ready.then(initGbxAnimations);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }
})();
